import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { api } from '../../context/AuthContext';
import StatusPill from '../../components/admin/StatusPill';
import { INTERNAL_KYC_UI } from '../../lib/featureFlags';
import { cn } from '../../lib/utils';

const TASK_LABELS = {
  subscription: 'Approve subscription',
  kyc: 'Review KYC',
  bank: 'Verify bank account',
  project: 'Complete project setup',
  instrument: 'Open offering',
  nav: 'Set asset NAV',
};

const money = (value) => (value ? `€${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—');

const dueLabel = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

export default function AdminTasksPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/workspace/attention?limit=50');
      setItems(res.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Identity and bank checks belong to the external compliance provider now, so they are
  // never queued as internal work.
  const queue = INTERNAL_KYC_UI ? items : items.filter((i) => !['kyc', 'bank'].includes(i.type));
  const visible = filter === 'all' ? queue : queue.filter((i) => i.type === filter);

  return (
    <div className="max-w-[1200px] space-y-6" data-testid="admin-tasks-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">Overview</p>
          <h1 className="text-xl md:text-2xl font-heading mt-1">Tasks &amp; approvals</h1>
          <p className="text-sm text-foreground-muted mt-0.5">
            Everything across Flippex waiting on an admin decision.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px] h-9" data-testid="tasks-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tasks</SelectItem>
              <SelectItem value="subscription">Subscriptions</SelectItem>
              {INTERNAL_KYC_UI && (
                <>
                  <SelectItem value="kyc">KYC</SelectItem>
                  <SelectItem value="bank">Bank accounts</SelectItem>
                </>
              )}
              <SelectItem value="project">Projects</SelectItem>
              <SelectItem value="instrument">Instruments</SelectItem>
              <SelectItem value="nav">NAV</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchTasks} data-testid="tasks-refresh-btn">
            <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <section className="bg-white rounded-xl border border-black/[0.06] overflow-hidden">
        {loading ? (
          <div className="py-20 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" />
          </div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center" data-testid="tasks-empty">
            <CheckCircle2 className="h-8 w-8 text-emerald-500 mx-auto mb-3" />
            <p className="text-sm font-medium">No open tasks</p>
            <p className="text-xs text-foreground-muted mt-1">
              Pending subscriptions and setup gaps appear here automatically.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-foreground-muted text-xs">
                  <th className="text-left font-medium px-5 py-2.5">Task</th>
                  <th className="text-left font-medium px-3 py-2.5">Entity</th>
                  <th className="text-left font-medium px-3 py-2.5 hidden md:table-cell">Project</th>
                  <th className="text-left font-medium px-3 py-2.5 hidden lg:table-cell">Priority</th>
                  <th className="text-right font-medium px-3 py-2.5 hidden sm:table-cell">Value</th>
                  <th className="text-left font-medium px-3 py-2.5 hidden lg:table-cell">Age</th>
                  <th className="text-left font-medium px-3 py-2.5">Status</th>
                  <th className="px-5 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {visible.map((item) => (
                  <tr
                    key={item.id}
                    className="border-t border-black/[0.05] hover:bg-slate-50/70"
                    data-testid={`task-row-${item.id}`}
                  >
                    <td className="px-5 py-3 font-medium whitespace-nowrap">{TASK_LABELS[item.type] || 'Review'}</td>
                    <td className="px-3 py-3 truncate max-w-[180px]">{item.entity || '—'}</td>
                    <td className="px-3 py-3 truncate max-w-[180px] hidden md:table-cell text-foreground-muted">
                      {item.project || '—'}
                    </td>
                    <td className="px-3 py-3 hidden lg:table-cell">
                      <StatusPill
                        status={item.priority === 'high' ? 'overdue' : 'pending'}
                        label={item.priority === 'high' ? 'High' : 'Normal'}
                      />
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums hidden sm:table-cell">{money(item.value)}</td>
                    <td className="px-3 py-3 hidden lg:table-cell text-foreground-muted">{dueLabel(item.date)}</td>
                    <td className="px-3 py-3"><StatusPill status={item.status} /></td>
                    <td className="px-5 py-3 text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-primary"
                        onClick={() => navigate(item.action_route)}
                        data-testid={`task-action-${item.id}`}
                      >
                        {item.action_label}
                        <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs text-foreground-muted">
        Tasks are generated from live platform state - pending subscriptions and project/instrument setup gaps.
        Identity checks sit with the external compliance provider. There is no separate task database.
      </p>
    </div>
  );
}
